// BestRx — City overview page (lists all services in a city).
import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import Footer from "@/components/landing/Footer";
import { useLeadModal } from "@/components/shared/LeadProvider";
import { CITIES, CITY_BY_SLUG, SERVICES, SERVICE_LIST } from "@/data/cities";
import { ALL_MEDS } from "@/data/medications";
import SEO, { breadcrumbSchema } from "@/components/shared/SEO";
import {
  NoInsuranceSection,
  HowItWorks4,
  RequestCallbackCTA,
  WhyBestRx,
  FAQAccordion,
  RelatedCluster,
  faqSchemaFrom,
  RED,
} from "@/components/shared/BestRxSections";

const PHONE = "844-703-1780";
const TEL = "tel:+18447031780";

function CityHeader({ onCta }) {
  return (
    <header className="absolute top-0 left-0 right-0 z-40 bg-white border-b border-black/10">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <a href="/" className="font-heavy text-black leading-none" style={{ fontSize: "clamp(24px, 2.6vw, 36px)" }} data-testid="brand-logo">
          BEST<span style={{ color: RED }}>RX</span>
        </a>
        <div className="flex items-center gap-3">
          <a href={TEL} className="hidden sm:inline-flex items-center gap-2 font-extrabold text-[13px]" style={{ color: RED }}>
            <Phone size={16} /> {PHONE}
          </a>
          <button
            onClick={onCta}
            className="inline-flex items-center gap-2 px-4 sm:px-6 py-3 font-extrabold uppercase text-[11.5px] sm:text-[13px] text-white tracking-[0.06em]"
            style={{ background: RED, boxShadow: "0 8px 20px rgba(225,29,42,0.30)" }}
            data-testid="header-cta"
          >
            Start Now <ArrowRight size={16} strokeWidth={2.6} />
          </button>
        </div>
      </div>
    </header>
  );
}

export default function CityOverviewPage() {
  const { city: citySlug } = useParams();
  const navigate = useNavigate();
  const city = CITY_BY_SLUG[citySlug];
  const { open } = useLeadModal();
  const onCta = () => open({ source: `city_overview_${citySlug}` });

  useEffect(() => { window.scrollTo(0, 0); }, [citySlug]);

  if (!city) {
    return (
      <main className="bg-white min-h-screen text-black">
        <SEO title="Not Found" path={`/locations/${citySlug}`} noindex />
        <CityHeader onCta={onCta} />
        <div className="pt-40 text-center px-6">
          <h1 className="font-heavy text-5xl">City not found</h1>
          <button onClick={() => navigate("/")} className="mt-8 px-6 py-3 font-extrabold text-white" style={{ background: RED }}>Back home →</button>
        </div>
        <Footer />
      </main>
    );
  }

  const path = `/locations/${citySlug}`;
  const faqItems = [
    { q: `Does BestRx serve ${city.name}, TX?`, a: `Yes — BestRx serves all of ${city.name} and the surrounding ${city.region} area. Provider review and shipping happen entirely online and to your home.` },
    { q: `How much do BestRx programs cost in ${city.name}?`, a: `New customers start as low as $35 their first week*. Monthly programs are flat-priced and clearly shown on every medication page. No hidden fees.` },
    { q: `Do I need insurance in ${city.name}?`, a: `No. BestRx is cash-pay. No copays, no deductibles, no claim denials.` },
    { q: `How fast can I get medication in ${city.name}?`, a: `Most ${city.name} patients receive their first month within 3–5 business days of clinician approval.` },
    { q: `Are BestRx clinicians licensed in Texas?`, a: `Yes. All clinicians prescribing through BestRx are licensed in the state of Texas.` },
  ];

  return (
    <main className="bg-white text-black overflow-x-hidden min-h-screen" data-testid={`city-overview-${citySlug}`}>
      <SEO
        title={`BestRx ${city.name}, ${city.state} · Weight Loss, TRT, Peptides`}
        path={path}
        description={`BestRx in ${city.name}, TX — Tirzepatide, Semaglutide, TRT, peptides and more. No insurance required. New customers start as low as $35 week 1*. Provider-reviewed, delivered to your door anywhere in ${city.name}.`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: city.name, path },
          ]),
          faqSchemaFrom(faqItems),
          {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            name: `BestRx ${city.name}`,
            url: `https://bestrx.online${path}`,
            telephone: "+1-844-703-1780",
            areaServed: { "@type": "City", name: city.name, containedIn: { "@type": "State", name: "Texas" } },
            medicalSpecialty: ["Weight Loss", "Men's Health", "Women's Health", "Peptide Therapy"],
          },
        ]}
      />
      <CityHeader onCta={onCta} />

      {/* HERO */}
      <section className="relative pt-28 sm:pt-32 md:pt-36 pb-12 md:pb-16" data-testid="city-overview-hero">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-extrabold tracking-[0.18em] mb-3 text-[12px] md:text-[13px] inline-flex items-center gap-2" style={{ color: RED }}>
              <MapPin size={14} /> SERVING {city.name.toUpperCase()}, {city.state}
            </p>
            <h1 className="font-heavy leading-[0.92]" style={{ fontSize: "clamp(40px, 6.2vw, 100px)" }} data-testid="city-overview-h1">
              <span className="text-black">BESTRX</span>
              <br />
              <span style={{ color: RED }}>{city.name.toUpperCase()}</span>.
            </h1>
            <p className="mt-5 text-[16px] md:text-[19px] text-black/75 leading-relaxed max-w-[640px]">
              Tirzepatide. Semaglutide. TRT. Peptides. Hair. ED treatment.
              All provider-prescribed, delivered to your door anywhere in {city.name}, with{" "}
              <span className="font-extrabold" style={{ color: RED }}>no insurance required</span> and
              new customers starting as low as <span className="font-extrabold" style={{ color: RED }}>$35 week 1*</span>.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={onCta}
                data-testid="city-overview-cta"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 font-extrabold text-white tracking-[0.08em] uppercase text-[14px]"
                style={{ background: RED, boxShadow: "0 12px 28px rgba(225,29,42,0.30)" }}
              >
                Start Now <ArrowRight size={16} strokeWidth={2.6} />
              </button>
              <a
                href={TEL}
                data-testid="city-overview-phone"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 font-extrabold tracking-[0.08em] uppercase text-[14px] border-2 border-black text-black"
              >
                <Phone size={16} /> {PHONE}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src="/assets/hero-meds.png?v=2" alt={`BestRx ${city.name}`} className="w-full h-auto max-h-[480px] object-contain" draggable={false} />
          </div>
        </div>
      </section>

      {/* SERVICE TILES */}
      <section className="bg-[#FAFAFA] py-14 md:py-20 border-y border-black/10">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <p className="font-extrabold tracking-[0.18em] mb-2 text-[12px]" style={{ color: RED }}>
              {city.name.toUpperCase()} PROGRAMS
            </p>
            <h2 className="font-heavy leading-tight" style={{ fontSize: "clamp(28px, 3.6vw, 52px)" }}>
              WHAT WE OFFER IN <span style={{ color: RED }}>{city.name.toUpperCase()}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICE_LIST.map((svc) => (
              <Link
                to={`/locations/${citySlug}/${svc.slug}`}
                key={svc.slug}
                className="rounded-md border bg-white p-6 hover:border-red-600 transition-colors flex flex-col"
                style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 6px 16px rgba(0,0,0,0.04)" }}
                data-testid={`city-svc-${svc.slug}`}
              >
                <p className="font-extrabold text-[11px] tracking-[0.16em] mb-2" style={{ color: RED }}>{city.name.toUpperCase()}, TX</p>
                <h3 className="font-heavy text-[20px] md:text-[24px] tracking-tight leading-tight mb-3">{svc.h1(city.name)}</h3>
                <p className="text-[13px] text-black/65 leading-snug mb-5 line-clamp-3">{svc.intro(city.name)}</p>
                <span className="mt-auto inline-flex items-center gap-1.5 font-extrabold text-[12px] tracking-[0.1em] uppercase" style={{ color: RED }}>
                  View Page <ArrowRight size={14} strokeWidth={2.6} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-heavy leading-tight mb-4" style={{ fontSize: "clamp(22px, 2.6vw, 34px)" }}>
              ABOUT <span style={{ color: RED }}>{city.name.toUpperCase()}</span>
            </h2>
            <p className="text-[15px] text-black/75 leading-relaxed mb-4">
              {city.name} is {city.note}, with a population of {city.population} and one of the fastest-growing demand profiles for telehealth wellness in Texas. From {city.landmarks?.slice(0, 3).join(", ")} to {city.landmarks?.slice(-1)[0]}, BestRx serves every neighborhood.
            </p>
            <p className="text-[15px] text-black/75 leading-relaxed">
              Whether you're a busy professional commuting through {city.landmarks?.[1] || "downtown"} or raising a family in {city.landmarks?.[2] || "the suburbs"}, we make weight loss and wellness treatment effortless: take a four-minute online assessment, get reviewed by a Texas-licensed clinician, and have your medication shipped discreetly to your address.
            </p>
          </div>
          <div>
            <p className="font-extrabold tracking-[0.18em] text-[11.5px] mb-3" style={{ color: RED }}>NEIGHBORHOODS WE SERVE IN {city.name.toUpperCase()}</p>
            <ul className="flex flex-wrap gap-2">
              {(city.landmarks || []).map((l) => (
                <li key={l} className="px-3 py-1.5 border border-black/15 text-[12.5px] font-bold text-black/80 rounded-sm">{l}</li>
              ))}
            </ul>
            {city.zips?.length > 0 && (
              <>
                <p className="font-extrabold tracking-[0.18em] text-[11.5px] mt-6 mb-3" style={{ color: RED }}>ZIP CODES</p>
                <p className="text-[13px] text-black/65 leading-relaxed">{city.zips.join(" · ")}</p>
              </>
            )}
          </div>
        </div>
      </section>

      <NoInsuranceSection />
      <HowItWorks4 />
      <WhyBestRx />
      <RequestCallbackCTA source={`city_overview_${citySlug}_callback`} />
      <FAQAccordion items={faqItems} title={`BestRx in ${city.name} — FAQ`} />
      <RelatedCluster medications={ALL_MEDS} cities={CITIES} currentCity={citySlug} />
      <Footer />
    </main>
  );
}
